import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { EmailTemplate } from '@spike/email-marketing-model';
import { fetchEmailTemplatesThunk } from '@spike/email-marketing-action';
import {
    FullWindowModal,
    FullWindowModalBody,
    FullWindowModalCloseButton,
    FullWindowModalHeader
} from 'components/UI/V3/Modals/FullWindowModal';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner';
import EmailTemplatePreviewModal from 'components/EmailMarketing/EmailTemplates/Gallery/EmailTemplatePreviewModal';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import styles from './EmailTemplatePicker.module.scss';

interface Props {
    selectedId: number | null;
    onSelect: (template: EmailTemplate) => void;
    onClose: () => void;
}

const formatDate = (iso: string): string => {
    if (!iso) return '';

    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Template chooser for an automation's email step.
 *
 * Fetches with scope 'own': these are templates to SEND, so the marketplace's
 * own saved designs are what's on offer. The email marketing gallery is not
 * reused here because it deliberately shows only the read-only system seeds,
 * which exist to be cloned rather than sent.
 */
const EmailTemplatePicker: FunctionComponent<Props> = ({ selectedId, onSelect, onClose }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const templates = useSelector((state: RootState) => state.emailTemplates.list);
    const loading = useSelector((state: RootState) => state.emailTemplates.loading);
    const error = useSelector((state: RootState) => state.emailTemplates.error);

    const [query, setQuery] = useState('');
    const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

    useEffect(() => {
        (dispatch as any)(fetchEmailTemplatesThunk(apiClientWrapper, 'own'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const list = templates ?? [];

        return needle ? list.filter(t => t.name.toLowerCase().includes(needle)) : list;
    }, [templates, query]);

    const renderBody = () => {
        if (loading && (templates ?? []).length === 0) return <Spinner />;

        if (error) {
            return <p className={styles.message}>Templates couldn&apos;t be loaded. Please try again in a moment.</p>;
        }

        if ((templates ?? []).length === 0) {
            return (
                <p className={styles.message}>
                    You don&apos;t have any email templates yet. Create one from Email Marketing → Templates, then come
                    back to use it here.
                </p>
            );
        }

        if (visible.length === 0) return <p className={styles.message}>No templates match “{query}”.</p>;

        return (
            <ul className={styles.list}>
                {visible.map(template => (
                    <li
                        key={template.id ?? template.uuid}
                        className={styles.row}
                    >
                        <button
                            type="button"
                            className={styles.rowMain}
                            onClick={() => setPreviewTemplate(template)}
                            title="Preview this template"
                        >
                            {/* sandboxed + inert: template HTML is user-authored. */}
                            <iframe
                                title={template.name}
                                className={styles.thumb}
                                sandbox=""
                                srcDoc={template.html}
                            />
                            <span className={styles.rowText}>
                                <span className={styles.rowName}>{template.name}</span>
                                <span className={styles.rowMeta}>Edited {formatDate(template.updatedAt)}</span>
                            </span>
                        </button>
                        <Button
                            label={template.id === selectedId ? 'Selected' : 'Select'}
                            variant={template.id === selectedId ? 'green' : 'black'}
                            size="small"
                            onClick={() => onSelect(template)}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <>
            <FullWindowModal>
                <FullWindowModalHeader>
                    <div className={styles.topBar}>
                        <img
                            className={styles.logo}
                            src="/images/logo/logo.png"
                            alt="Cuddles"
                        />
                        <h1 className={styles.title}>Select a Template</h1>
                        <FullWindowModalCloseButton
                            onClick={onClose}
                            ariaLabel="Close template picker"
                            className={styles.closeButton}
                        />
                    </div>
                </FullWindowModalHeader>

                <FullWindowModalBody>
                    <div className={styles.body}>
                        <div className={styles.searchRow}>
                            <FontAwesomeIcon
                                icon={faMagnifyingGlass}
                                className={styles.searchIcon}
                            />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search by email name"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                            />
                        </div>

                        {renderBody()}
                    </div>
                </FullWindowModalBody>
            </FullWindowModal>

            <EmailTemplatePreviewModal
                template={previewTemplate}
                onClose={() => setPreviewTemplate(null)}
            />
        </>
    );
};

export default EmailTemplatePicker;
